import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Hourglass, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";

const NET_WORTH = ["<1M", "1-5M", "5-25M", "25-100M", "100M+"];
const ID_TYPES = [
  { v: "passport", l: "Passport" },
  { v: "national_id", l: "National ID" },
  { v: "drivers_license", l: "Driver's License" },
];

const Kyc = () => {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: "", date_of_birth: "", nationality: "", residential_address: "",
    country: "", id_type: "passport", id_number: "", occupation: "",
    source_of_funds: "", net_worth_bracket: "1-5M",
    politically_exposed: false, declarations_accepted: false,
  });

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/kyc/me").then(({ data }) => {
      setStatus(data.kyc_status);
      setSubmission(data.submission);
      if (data.submission) {
        setForm((f) => ({ ...f, full_name: data.submission.full_name || user.name }));
      } else {
        setForm((f) => ({ ...f, full_name: user.name }));
      }
    }).catch(() => {});
  }, [user]);

  if (user === null) return <div className="pt-40 text-center text-white/40">Loading…</div>;
  if (user === false) {
    return (
      <div className="pt-40 max-w-md mx-auto px-6 text-center">
        <h1 className="font-heading text-4xl mb-4">Sign in required</h1>
        <Link to="/login" className="inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-9 py-4">Sign In</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.declarations_accepted) { toast.error("Please accept the declarations"); return; }
    setBusy(true);
    try {
      await api.post("/kyc/submit", form);
      toast.success("KYC submitted. Review typically within 24 hours.");
      const { data } = await api.get("/kyc/me");
      setStatus(data.kyc_status); setSubmission(data.submission);
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const Status = ({ icon, color, title, body }) => (
    <div className={`bg-onyx border ${color} p-10 mb-12 text-center`}>
      <div className="mx-auto mb-4">{icon}</div>
      <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-3">{title}</div>
      <p className="text-white/60 max-w-md mx-auto leading-relaxed">{body}</p>
    </div>
  );

  return (
    <div data-testid="kyc-page" className="pt-32">
      <Seo title="Bidder Verification" description="KYC verification for OPIKJEWELS auction participation." path="/kyc" noIndex />
      <section className="max-w-3xl mx-auto px-6 mb-12 text-center">
        <ShieldCheck className="mx-auto text-gold mb-6" size={40} />
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Bidder Verification</p>
        <h1 className="font-heading font-light text-5xl md:text-6xl mb-6">Become a verified bidder.</h1>
        <p className="text-white/60 leading-relaxed">OPIKJEWELS operates under Indonesian AML/KYC standards (PPATK) and equivalent international compliance frameworks. All bidders complete a brief verification before participating in auctions or private sales.</p>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-32">
        {status === "approved" && (
          <Status icon={<CheckCircle2 className="text-gold" size={48} />} color="border-gold/50" title="Verified Bidder"
                  body="Your account is approved for bidding and private acquisitions. Welcome to the floor." />
        )}
        {status === "pending" && (
          <Status icon={<Hourglass className="text-yellow-400" size={48} />} color="border-yellow-400/40" title="Under Review"
                  body="Your submission is in review. Our compliance team typically responds within 24 hours." />
        )}
        {status === "rejected" && (
          <Status icon={<XCircle className="text-red-400" size={48} />} color="border-red-500/40" title="Verification Declined"
                  body="Your submission was not approved. Please contact concierge to resolve." />
        )}

        {(status === "none" || status === "rejected") && (
          <form onSubmit={submit} className="bg-onyx border border-white/10 p-8 md:p-10 space-y-6">
            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-2">Personal Information</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Full Legal Name" testid="kyc-name" required value={form.full_name} onChange={(v) => setForm({...form, full_name: v})} />
              <Field label="Date of Birth" testid="kyc-dob" required type="date" value={form.date_of_birth} onChange={(v) => setForm({...form, date_of_birth: v})} />
              <Field label="Nationality" testid="kyc-nat" required value={form.nationality} onChange={(v) => setForm({...form, nationality: v})} />
              <Field label="Country of Residence" testid="kyc-country" required value={form.country} onChange={(v) => setForm({...form, country: v})} />
            </div>
            <Field label="Residential Address" testid="kyc-addr" required value={form.residential_address} onChange={(v) => setForm({...form, residential_address: v})} />

            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold pt-4">Identification</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/40 block mb-3">ID Type</label>
                <select data-testid="kyc-idtype" required value={form.id_type} onChange={(e) => setForm({...form, id_type: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold text-white">
                  {ID_TYPES.map((o) => <option key={o.v} value={o.v} className="bg-noir">{o.l}</option>)}
                </select>
              </div>
              <Field label="ID Number" testid="kyc-idnum" required value={form.id_number} onChange={(v) => setForm({...form, id_number: v})} />
            </div>

            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold pt-4">Financial Profile</div>
            <Field label="Occupation" testid="kyc-occ" required value={form.occupation} onChange={(v) => setForm({...form, occupation: v})} />
            <Field label="Source of Funds" testid="kyc-funds" required value={form.source_of_funds} onChange={(v) => setForm({...form, source_of_funds: v})} placeholder="e.g. Business income, investments, inheritance" />
            <div>
              <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/40 block mb-3">Declared Net Worth (USD)</label>
              <select data-testid="kyc-networth" required value={form.net_worth_bracket} onChange={(e) => setForm({...form, net_worth_bracket: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold text-white">
                {NET_WORTH.map((o) => <option key={o} value={o} className="bg-noir">{o}</option>)}
              </select>
            </div>

            <label className="flex items-start gap-3 pt-2 cursor-pointer">
              <input data-testid="kyc-pep" type="checkbox" checked={form.politically_exposed} onChange={(e) => setForm({...form, politically_exposed: e.target.checked})} className="mt-1.5 accent-yellow-400" />
              <span className="text-white/70 text-sm">I am a politically exposed person (or close family member of one).</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="kyc-decl" type="checkbox" required checked={form.declarations_accepted} onChange={(e) => setForm({...form, declarations_accepted: e.target.checked})} className="mt-1.5 accent-yellow-400" />
              <span className="text-white/70 text-sm">I declare the above information is true and accurate. I consent to OPIKJEWELS verifying it for AML/KYC compliance under Indonesian law and applicable international standards.</span>
            </label>

            <button data-testid="kyc-submit" disabled={busy} className="w-full bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs py-4 hover:bg-ivory transition-colors duration-500 disabled:opacity-50">
              {busy ? "Submitting…" : "Submit for Verification"}
            </button>
          </form>
        )}

        {submission && status !== "none" && (
          <div className="mt-10 bg-onyx border border-white/10 p-8">
            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Submission</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 text-sm">
              {[
                ["Name", submission.full_name], ["Date of Birth", submission.date_of_birth],
                ["Nationality", submission.nationality], ["Country", submission.country],
                ["ID", `${submission.id_type} · ${submission.id_number}`],
                ["Occupation", submission.occupation],
                ["Net Worth", submission.net_worth_bracket],
                ["Submitted", new Date(submission.submitted_at).toLocaleString()],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1">{k}</div>
                  <div className="text-ivory">{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

const Field = ({ label, testid, value, onChange, type = "text", required, placeholder }) => (
  <div>
    <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/40 block mb-3">{label}</label>
    <input data-testid={testid} type={type} required={required} value={value} placeholder={placeholder}
           onChange={(e) => onChange(e.target.value)}
           className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold text-white" />
  </div>
);

export default Kyc;
